import { debounce } from 'lodash'
import { createGlobalService, deleteGlobalServiceByID, readGlobalServices, updateGlobalServiceByID } from '../utils/api'
import { useCallback, useEffect, useState } from 'react'
import { globalServicesFormTemplate } from '../templates/form-template'
import { GlobalServicesEnums } from '../utils/enums'
import { message } from 'antd'

export const useGlobalServices = () => {
  const [currentRecord, setCurrentRecord] = useState(null)
  const [facet, setFacet] = useState(1) // 1 = table view, 2 = form view
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [services, setServices] = useState([])
  const [searchQry, setSearchQry] = useState('')
  const [loading, setLoading] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [additionalFilter, setAdditionalFilter] = useState({
    categoryFilter: [],
    rateFilter: [null, null],
    durationFilter: null
  })

  useEffect(() => {
    getServices()
    // eslint-disable-next-line
  }, [searchQry])

  // eslint-disable-next-line
  const debouncedGetServices = useCallback(
    debounce((qry) => {
      if (qry) {
        setSearchQry(qry)
      } else {
        setSearchQry('')
      }
    }, 500),
    []
  )

  const handleAddFilterChange = (type, value) => {
    let tmpAddFilters = JSON.parse(JSON.stringify(additionalFilter))
    switch (type) {
      case 'category':
        if (tmpAddFilters.categoryFilter.includes(value)) {
          tmpAddFilters.categoryFilter = tmpAddFilters.categoryFilter.filter((f) => f !== value)
        } else {
          tmpAddFilters.categoryFilter.push(value)
        }
        break
      case 'rate':
        tmpAddFilters.rateFilter = value
        break
      case 'duration':
        tmpAddFilters.durationFilter = value
        break
      default:
        break
    }
    setAdditionalFilter(tmpAddFilters)
  }

  const configureAdditionalFilters = () => {
    let addFilters = {}
    if (additionalFilter.categoryFilter.length > 0) {
      addFilters = {
        ...addFilters,
        category: { $in: additionalFilter.categoryFilter }
      }
    }

    if (additionalFilter.rateFilter[0] && additionalFilter.rateFilter[1]) {
      addFilters = {
        ...addFilters,
        defaultRate: { $gte: additionalFilter.rateFilter[0], $lte: additionalFilter.rateFilter[1] }
      }
    }

    if (additionalFilter.durationFilter) {
      addFilters = {
        ...addFilters,
        defaultDurationMin: additionalFilter.durationFilter
      }
    }
    return addFilters
  }

  const getServices = async () => {
    try {
      let filter = { ...configureAdditionalFilters() }

      if (searchQry) {
        filter = {
          ...filter,
          $or: [
            {
              $or: [
                {
                  $expr: {
                    $regexMatch: {
                      input: { $toLower: '$name' },
                      regex: searchQry.toLowerCase(),
                      options: 'i'
                    }
                  }
                },
                {
                  $expr: {
                    $regexMatch: {
                      input: { $toLower: '$description' },
                      regex: searchQry.toLowerCase(),
                      options: 'i'
                    }
                  }
                },
                {
                  $expr: {
                    $regexMatch: {
                      input: { $toLower: '$defaultRate' },
                      regex: searchQry.toLowerCase(),
                      options: 'i'
                    }
                  }
                }
              ]
            }
          ]
        }
      }
      setLoading(true)
      const response = await readGlobalServices(filter)
      setServices(response || [])
    } catch (error) {
      message.error('Failed to load services')
    } finally {
      setLoading(false)
    }
  }

  const addRecord = async () => {
    setCurrentRecord({ ...globalServicesFormTemplate, isNew: true })
    setFacet(2)
  }

  const editRecord = async (record) => {
    setCurrentRecord({ ...record, isNew: false })
    setFacet(2)
  }

  const deleteRecord = async (recordId) => {
    try {
      setLoading(true)
      await handleGlobalServicesApi(GlobalServicesEnums.routeKeys.DELETE_BY_ID, { recordId })
      message.success('Service deleted successfully')
      await getServices() // Refresh the list
    } catch (error) {
      message.error('Failed to delete service')
    } finally {
      setLoading(false)
    }
  }

  const handleGlobalServicesApi = async (action, props) => {
    let apiCall = () => {}
    const { data, recordId } = props || {}

    switch (action) {
      case GlobalServicesEnums.routeKeys.CREATE:
        apiCall = () => createGlobalService(data)
        break
      case GlobalServicesEnums.routeKeys.UPDATE_BY_ID:
        apiCall = () => updateGlobalServiceByID(recordId, data)
        break
      case GlobalServicesEnums.routeKeys.DELETE_BY_ID:
        apiCall = () => deleteGlobalServiceByID(recordId)
        break
    }

    try {
      setFormLoading(true)
      const response = await apiCall()

      if (action === GlobalServicesEnums.routeKeys.CREATE) {
        message.success('Service created successfully')
      } else if (action === GlobalServicesEnums.routeKeys.UPDATE_BY_ID) {
        message.success('Service updated successfully')
      }

      // Return to table view and refresh
      setFacet(1)
      await getServices()

      return response
    } catch (error) {
      if (action === GlobalServicesEnums.routeKeys.CREATE) {
        message.error('Failed to create service')
      } else if (action === GlobalServicesEnums.routeKeys.UPDATE_BY_ID) {
        message.error('Failed to update service')
      }
      throw error
    } finally {
      setFormLoading(false)
    }
  }

  return {
    facet,
    setFacet,
    page,
    setPage,
    pageSize,
    setPageSize,
    services,
    debouncedGetServices,
    currentRecord,
    addRecord,
    editRecord,
    deleteRecord,
    handleGlobalServicesApi,
    getServices,
    loading,
    formLoading,
    handleAddFilterChange
  }
}
